import { useId } from "react";
import { GfgButton } from "@/components/ui/GfgButton";

/**
 * The primary Send / Schedule CTA. Mirrors the prototype's
 * `#launchCampaignBtn` (Campaign-Builder-Master-Prototype-Stage2-Revisions.html,
 * markup ~line 11074, CSS ~line 8043, `updateLaunchButtonState()` ~line 23870).
 *
 * Renders the shared `GfgButton` (variant="primary") rather than a bespoke
 * button (review, 2026-08-17); the classes below only widen it to the buy box
 * and carry the launch-specific not-ready treatment, which is not a GfgButton
 * variant because it is not a native-disabled state — see below.
 *
 * Until the order is ready — recipients added and a valid payment method, or
 * enough credits that nothing is owed — the CTA is greyed out and does nothing
 * (review, Ivan 2026-08-14). `ready` comes from `isLaunchReady()`.
 *
 * "Disabled" here is `aria-disabled` plus a no-op handler, NOT the native
 * `disabled` attribute: the button stays focusable so `aria-describedby` can
 * say why it is inert, rather than leaving a silently dead control assistive
 * tech skips over. The reason is either rendered here (`reason`) or already on
 * screen and referenced by id (`reasonId`) — the inline "Add recipients to
 * continue" line is the latter, so the message is not duplicated.
 *
 * The expiry acknowledgment deliberately does NOT feed `ready`: clicking while
 * ready but unacknowledged is a valid click that the caller answers with an
 * inline error, exactly as `openCampaignCheckout()` does at ~line 23985.
 */
export interface LaunchButtonProps {
  /** "Send Gifts Now" or "Schedule Gifts" — see `launchButtonLabel()` in src/lib/checkout.ts. */
  label: string;
  ready: boolean;
  /** Reason text to render under the CTA. Omit when passing `reasonId`. */
  reason?: string;
  /** Id of a reason element the caller already renders. Takes precedence over `reason`. */
  reasonId?: string;
  onLaunch: () => void;
}

export default function LaunchButton({
  label,
  ready,
  reason,
  reasonId,
  onLaunch,
}: LaunchButtonProps) {
  const ownReasonId = useId();
  const describedBy = ready ? undefined : (reasonId ?? (reason ? ownReasonId : undefined));

  return (
    <div className="mt-5">
      <GfgButton
        type="button"
        variant="primary"
        aria-disabled={!ready}
        aria-describedby={describedBy}
        // Inert while not ready. The checkout column guards this too, but a CTA that looks
        // dead and still fires is worse than either state on its own.
        onClick={ready ? onLaunch : undefined}
        className={[
          "w-full py-4 text-sm tracking-wide",
          ready
            ? ""
            : // Grey label on the grey fill, not white — white on #e0e0e0 is ~1.4:1 and reads
              // as an ENABLED button that simply lost its colour. Not GfgButton's
              // `disabled:` styling on purpose: that requires the native attribute.
              "cursor-not-allowed bg-smoke text-graphite hover:bg-smoke",
        ].join(" ")}
      >
        {label}
      </GfgButton>
      {!ready && !reasonId && reason && (
        <p id={ownReasonId} className="mt-2 text-center font-lato text-xs text-graphite">
          {reason}
        </p>
      )}
    </div>
  );
}
